import React, { memo, useCallback, type ReactNode, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import isNil from 'lodash/isNil';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { saveField as saveFieldClassic } from '@atlassian/jira-issue-field-classic-story-point/src/services/index.tsx';
import { INLINE, LOZENGE } from '@atlassian/jira-issue-field-number/src/common/types.tsx';
import { IssueNumberFieldBoundary } from '@atlassian/jira-issue-field-number/src/ui/async.tsx';
import type IssueNumberFieldType from '@atlassian/jira-issue-field-number/src/ui/index.tsx';
import { STORY_POINTS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { STORY_POINT_ESTIMATE_CF_ANALYTIC_TYPE } from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import { STORY_POINTS } from '../../../../../model/constants.tsx';
import type { EstimationType } from '../../../../../model/types.tsx';
import {
	cancelInlineEditStoryPointInteractionMark,
	setInlineEditStoryPointInteractionStartMark,
	setInlineEditStoryPointInteractionStopMark,
} from '../../../../../services/performance/interaction-metric-inline-edit.tsx';
import { cardEstimateSet } from '../../../../../state/actions/estimate/index.tsx';
import { refetchVersions } from '../../../../../state/actions/version/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../../../state/index.tsx';
import { backlogHasEstimates, getCards } from '../../../../../state/selectors/cards/index.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import SLAExperienceTracker from '../../../../sla-experience-tracker/index.tsx';
import { CARD_CONTAINER_SELECTOR } from '../../constants.tsx';
import messages from '../../messages.tsx';
import EstimateFieldWrapper from '../estimate-field-wrapper/index.tsx';
import StoryPointFieldStatic from './story-point-field-static/index.tsx';

// Exported for testing
export const IssueNumberField = lazyForPaint<typeof IssueNumberFieldType>(
	() =>
		import(/* webpackChunkName: "async-issue-field-number" */ '@atlassian/jira-issue-field-number'),
);

export type Props = {
	issueId: number;
	issueKey: string;
	storyPointFieldId: string | null;
	estimationType: EstimationType;
	isUserAtLeastMember: boolean;
	shouldRenderRichCard: boolean;
	shouldRenderStoryPointColumn: boolean;
};

const StoryPointField = ({
	issueKey,
	storyPointFieldId,
	estimationType,
	issueId,
	isUserAtLeastMember,
	shouldRenderRichCard,
	shouldRenderStoryPointColumn,
}: Props) => {
	const isCompanyManaged = useIsCMPBacklog();
	const dispatch = useBacklogDispatch();
	const { formatMessage, formatNumber } = useIntl();
	const [isEditing, setIsEditing] = useState(false);
	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const storyPointKey = storyPointFieldId || '';
	const [storyPointFieldValue] = useFieldValue({
		issueKey,
		fieldKey: storyPointKey,
	});

	const ariaLabelWithValue = storyPointFieldValue
		? formatMessage(messages.storyPointsAriaLabel, {
				storyPointValue: formatNumber(storyPointFieldValue),
			})
		: formatMessage(messages.storyPointsAriaLabelNullValue);

	// I think this is more correct since hasEstimate only checks THIS issue but there could be
	// other issues on the backlog that do have estimates. That will mess up the layout
	// and shift all the content slightly across to make space for the story point field
	const shouldRenderEstimatesColumn = useBacklogSelector(backlogHasEstimates);
	const hasEstimate = useBacklogSelector((state) => !!getCards(state)[String(issueId)]?.estimate);

	const saveCompanyManaged = useCallback(
		(iKey: string, fieldKey: string, newValue: number | null, baseURL: string) =>
			saveFieldClassic(String(issueId), fieldKey, newValue, baseURL),
		[issueId],
	);

	const handleEdit = useCallback(() => {
		if (storyPointKey && fg('one_event_rules_them_all_fg')) {
			getUpdateAnalyticsFlowHelper().fireAnalyticsStart(storyPointKey, {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: FIELD_TYPE_MAP[STORY_POINTS_TYPE],
					isInlineEditing: true,
				},
			});
		}
		setIsEditing(true);
	}, [createAnalyticsEvent, storyPointKey]);

	const handleConfirm = useCallback(() => {
		setInlineEditStoryPointInteractionStartMark();
		setIsEditing(false);
	}, []);

	const handleUpdate = useCallback(
		(value: number | unknown) => {
			setInlineEditStoryPointInteractionStopMark(isCompanyManaged);
			dispatch(refetchVersions());
			if (value !== undefined && typeof value === 'number') {
				dispatch(cardEstimateSet(issueId, { storyPoints: value }));
			}
			if (isCompanyManaged) {
				triggerEpicPanelRefetch();
			}
			setIsEditing(false);
		},
		[dispatch, isCompanyManaged, triggerEpicPanelRefetch, issueId],
	);

	const handleFailure = useCallback(() => {
		cancelInlineEditStoryPointInteractionMark();
		setIsEditing(false);
	}, []);

	const handleCancel = useCallback(() => {
		cancelInlineEditStoryPointInteractionMark();
		setIsEditing(false);
	}, [setIsEditing]);

	if (
		!shouldRenderStoryPointColumn &&
		(!storyPointKey || estimationType !== STORY_POINTS || shouldRenderEstimatesColumn)
	) {
		return null;
	}

	const isStoryPointAccessible = !isNil(storyPointFieldValue);

	const issueStoryPointFieldStatic = (
		<StoryPointFieldStatic
			value={storyPointFieldValue ?? undefined}
			hasStoryPoints={hasEstimate}
			shouldRenderStoryPointColumn={shouldRenderStoryPointColumn}
		/>
	);

	const renderStorypointField = () => (
		// We should always render the container otherwise the layout gets all messed up
		<StoryPointFieldWrapper shouldRenderStoryPointColumn={shouldRenderStoryPointColumn}>
			<EstimateFieldWrapper
				tooltipLabel={formatMessage(messages.storyPointsTooltip)}
				ariaLabel={ariaLabelWithValue}
				tooltipPosition={isEditing ? 'top' : 'bottom'}
			>
				<StoryPointFieldContainer
					hasValue={isStoryPointAccessible}
					useInlineEditIndexFix={fg('tnk-693-basic-estimate-inline-edit-input')}
				>
					{hasEstimate && (
						<IssueNumberField
							issueKey={issueKey}
							fieldKey={storyPointKey}
							fieldFocusStyle={LOZENGE}
							fieldEditStyle={INLINE}
							onEdit={handleEdit}
							onUpdate={handleUpdate}
							onConfirm={handleConfirm}
							onFailure={handleFailure}
							onCancel={handleCancel}
							saveField={isCompanyManaged ? saveCompanyManaged : undefined}
						/>
					)}
				</StoryPointFieldContainer>
			</EstimateFieldWrapper>
		</StoryPointFieldWrapper>
	);

	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<IssueNumberFieldBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={issueStoryPointFieldStatic}
			>
				<SLAExperienceTracker
					experienceTarget="inline story point"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<ContextualAnalyticsData
						attributes={{ fieldName: STORY_POINT_ESTIMATE_CF_ANALYTIC_TYPE }}
					>
						{renderStorypointField()}
					</ContextualAnalyticsData>
				</SLAExperienceTracker>
			</IssueNumberFieldBoundary>
		);
	}

	return issueStoryPointFieldStatic;
};

export default memo<Props>(StoryPointField);

type StoryPointFieldProps = {
	hasValue: boolean;
	children: ReactNode;
	useInlineEditIndexFix: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const StoryPointFieldContainer = styled2.div<StoryPointFieldProps>(
	{
		/* NOTE: cursor stylings for hover behavior should be handled inside the field component, not here */
		cursor: 'default',
		display: 'flex',

		/* build our own focus effect using box-shadow because AK's focus takes more space and doesn't respond to active state */
		'&:focus, &:focus-within': {
			outline: 'none',
		},
		/* undo hide if card is focused or hovered */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:hover &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:active &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus &, ` +
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus-within &`]: {
			position: 'inherit',
			opacity: 'inherit',
			pointerEvents: 'inherit',
		},

		/*
		 * NOTE: The AK component used by AsyncIssueNumberField is from an older
		 * version. The following styling adjustments may be subject to
		 * change/remove after AK components are bumped in BENTO
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'*': {
			/* AK badge has several inline-block elements which result in misalignment, so we override display: inline-block to display: flex */
			display: 'flex',

			/*
			 * NOTE: This is used to remove the focus blue ring around an invisible
			 * element inside AK component (most likely a screen reader label) when
			 * the story point component is focussed and hovered at the same time
			 */
			'&:empty': {
				boxShadow: 'none',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > div > div': {
			/* remove a margin-left: -8px offset from AK badge */
			marginLeft: 0,

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"&[data-component-selector='jira-issue-field-inline-edit-readonly-view-container']": {
				/*
				 * NonEditableMargin overrides.
				 * The top margin here negates the negative margin being applied to the parent (where it's used to create extra space for the input element).
				 * This component's original margin is intended for use within bento. So, we set all the other values to 0 too.
				 */

				marginTop: token('space.100'),
				marginRight: 0,
				marginBottom: 0,
				marginLeft: 0,
			},

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > div > div': {
				/* AK badge has a higher precedence display: inline-block on this level, so we override it specifically here */
				display: 'flex',
			},
		},
	},
	// Fix Inline Edit as per https://hello.jira.atlassian.cloud/browse/TNK-693
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(
		props /* Used dynamic style because the original zIndex: 200 of tick/cross boxes are overwritten, and they don't show with FG off */,
	) =>
		props.useInlineEditIndexFix
			? {
					'[data-testid="issue-field-number.ui.issue-field-number--container"] > form': {
						'div[tabindex="-1"]': {
							zIndex: 2 /* z-index for the tick and cross buttons */,
						},
						'div[role="presentation"]': {
							zIndex: 1 /* z-index for the Inline Edit container */,
						},
					},
				}
			: {},
);

// empty column for the cards that without storyPoint
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StoryPointFieldWrapper = styled2.div<{
	shouldRenderStoryPointColumn: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>({}, (props) =>
	props.shouldRenderStoryPointColumn
		? {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				minWidth: `${gridSize * 5.7}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				maxWidth: `${gridSize * 5.7}px`,
			}
		: {},
);
